import { isDefined } from '@app/utils/is';
import { Selects } from '@typings';
import { without } from 'ramda';
import React from 'react';

import { OPTIONS_FADE_DURATION } from '../Options';

interface Context {
  activeOptionPosition?: Nullable<number>;
  close: () => void;
  containerRef?: React.RefObject<HTMLElement>;
  dataTestId?: string;
  describedById?: string;
  errorMessageId?: string;
  focusedPillPosition: Nullable<number>;
  focusPill: (value: Nullable<number>) => void;
  focusedOptionPosition: Nullable<number>;
  focusOption: (value: number) => void;
  resetFocusedOption: VoidFunction;
  handleSelect: (value: Selects.NonNullableValue) => void;
  hoveredOptionPosition: Nullable<number>;
  hoverOption: (value: number) => void;
  isDisabled: boolean;
  isInvalid: boolean;
  isLoading: boolean;
  isLoadingOptions: boolean;
  isMultiple?: boolean;
  isOpen: boolean;
  onBlur?: (event: React.FocusEvent) => void;
  onLoadMore?: () => void;
  open: () => void;
  optionsId: string;
  overflowContainer: boolean;
  placeholder?: string;
  selection: Selects.Selection;
  showArrow: boolean;
  size: Selects.Size;
  toggleIsOpen: () => void;
  triggerId: string;
}

export const useSelectContext = () => {
  const context = React.useContext(SelectContext);

  if (!isDefined(context)) {
    throw new Error('SelectContext can not be used outside the scope of SelectContextProvider');
  }

  return context;
};

export const SelectContext = React.createContext<Context | null>(null);

export const SelectContextProvider = ({
  allowClear = false,
  children,
  containerRef,
  dataTestId,
  describedById,
  errorMessageId,
  id,
  isDisabled = false,
  isInvalid = false,
  isLoading = false,
  isLoadingOptions = false,
  isMultiple,
  name,
  onBlur,
  onChange,
  onLoadMore,
  onOpen,
  overflowContainer = false,
  placeholder,
  showArrow = true,
  size = 'medium',
  value,
}: Selects.CommonProps) => {
  const reactId = React.useId();
  const triggerId = id ?? name ?? reactId;
  const optionsId = `${triggerId}-options`;

  const [selection, setSelection] = React.useState(value);
  const [isOpen, setIsOpen] = React.useState(false);
  const [focusedPillPosition, setFocusedPillPosition] = React.useState<Nullable<number>>(null);
  const [focusedOptionPosition, setFocusedOptionPosition] = React.useState<Nullable<number>>(null);
  const [hoveredOptionPosition, setHoveredOptionPosition] = React.useState<Nullable<number>>(null);

  React.useEffect(() => {
    setSelection(value);
  }, [value]);

  React.useEffect(() => {
    if (isOpen) {
      return;
    }

    const timeout = setTimeout(() => {
      setFocusedOptionPosition(null);
      setHoveredOptionPosition(null);
    }, OPTIONS_FADE_DURATION);

    return () => {
      clearTimeout(timeout);
    };
  }, [isOpen]);

  const open = React.useCallback(() => {
    if (isOpen) {
      return;
    }

    setFocusedPillPosition(null);
    setIsOpen(true);
    onOpen?.();
  }, [isOpen, onOpen]);

  const close = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleIsOpen = React.useCallback(() => {
    if (isDisabled) {
      return;
    }

    isOpen ? close() : open();
  }, [close, isDisabled, isOpen, open]);

  const hoverOption = React.useCallback((index: number) => {
    setHoveredOptionPosition(index);
    setFocusedOptionPosition(null);
  }, []);

  const focusOption = React.useCallback((index: number) => {
    setHoveredOptionPosition(index);
    setFocusedOptionPosition(index);
  }, []);

  const resetFocusedOption = React.useCallback(() => {
    setFocusedOptionPosition(null);
  }, []);

  const focusPill = React.useCallback(
    (index: number | null) => {
      setFocusedPillPosition(index);

      if (isDefined(index)) {
        close();
      }
    },
    [close],
  );

  const handleSelect = React.useCallback(
    (selectedValue: Selects.NonNullableValue) => {
      const isMultipleSelection = Array.isArray(selection);
      const shouldPreventOnChange = selectedValue === selection && !allowClear;

      if (shouldPreventOnChange) {
        setIsOpen(false);

        return;
      }

      if (isMultiple && isMultipleSelection) {
        const newSelection = selection.includes(selectedValue) ? without([selectedValue], selection) : [...selection, selectedValue];

        setSelection(newSelection);
        onChange(newSelection);
      }

      if (!isMultiple && !isMultipleSelection) {
        const shouldClearSelection = selectedValue === selection && allowClear;
        const newSelection = shouldClearSelection ? null : selectedValue;

        setSelection(newSelection);
        onChange(newSelection);
      }

      if (!isMultipleSelection) {
        setIsOpen(false);
      }
    },
    [isMultiple, allowClear, onChange, selection],
  );

  return (
    <SelectContext.Provider
      value={{
        activeOptionPosition: focusedOptionPosition ?? hoveredOptionPosition,
        close,
        containerRef,
        dataTestId,
        describedById,
        errorMessageId,
        focusOption,
        focusPill,
        focusedOptionPosition,
        focusedPillPosition,
        handleSelect,
        hoverOption,
        hoveredOptionPosition,
        isDisabled,
        isInvalid,
        isLoading,
        isLoadingOptions,
        isMultiple,
        isOpen,
        onBlur,
        onLoadMore,
        open,
        optionsId,
        overflowContainer,
        placeholder,
        resetFocusedOption,
        selection,
        showArrow,
        size,
        toggleIsOpen,
        triggerId,
      }}
    >
      {children}
    </SelectContext.Provider>
  );
};
